import { AppConfig, Layer, LayerElement, NFTMetadata, GeneratedImage, BlendMode } from '../types';
import { UniqueNFT } from '../types/index';
import sha1 from 'crypto-js/sha1';

const DNA_DELIMITER = '-';

// Function to clean name (remove extension and weight)
const cleanName = (str: string, rarityDelimiter: string): string => {
  const nameWithoutExtension = str.slice(0, str.lastIndexOf('.'));
  return nameWithoutExtension.split(rarityDelimiter)[0];
};

// Function to get rarity weight
const getRarityWeight = (str: string, rarityDelimiter: string): number => {
  const nameWithoutExtension = str.slice(0, str.lastIndexOf('.'));
  const weightStr = nameWithoutExtension.split(rarityDelimiter).pop();
  
  if (!weightStr) return 1;
  
  const weight = Number(weightStr);
  return isNaN(weight) ? 1 : weight;
};

// Check if DNA is unique
const isDnaUnique = (dnaList: Set<string>, dna: string): boolean => {
  return !dnaList.has(dna);
};

// Create random DNA
const createDna = (layers: Layer[]): string => {
  console.log('Début de la création du DNA');
  let dnaSequence = [];
  let attempts = 0;
  const maxAttempts = 100;

  while (attempts < maxAttempts) {
    console.log(`Tentative ${attempts + 1} de création du DNA`);
    dnaSequence = [];
    let isValid = true;

    for (const layer of layers) {
      console.log(`Sélection dans la couche: ${layer.name}`);
      const totalWeight = layer.elements.reduce((acc, element) => acc + element.weight, 0);
      let random = Math.floor(Math.random() * totalWeight);
      
      for (const element of layer.elements) {
        random -= element.weight;
        if (random < 0) {
          console.log(`Élément sélectionné dans ${layer.name}: ${element.name}`);
          dnaSequence.push(`${layer.id}:${element.id}:${element.name}`);
          break;
        }
      }
    }

    // Vérifier les restrictions
    const selectedElements = dnaSequence.map(seq => {
      const [layerId, elementId] = seq.split(':').map(Number);
      const layer = layers.find(l => l.id === layerId);
      const element = layer?.elements.find(e => e.id === elementId);
      return element;
    }).filter((el): el is LayerElement => el !== undefined);

    console.log('Vérification des restrictions pour:', selectedElements.map(el => el.name));
    
    // Vérifier les incompatibilités
    for (const element of selectedElements) {
      if (element.restrictions?.incompatibleWith) {
        console.log('Vérification des incompatibilités pour:', element.name);
        for (const restriction of element.restrictions.incompatibleWith) {
          const incompatibleLayer = layers.find(l => l.id === restriction.layerId);
          if (!incompatibleLayer) continue;

          const selectedElementFromLayer = selectedElements.find(
            el => incompatibleLayer.elements.some(layerEl => layerEl.id === el.id)
          );

          if (selectedElementFromLayer && restriction.elementIds.includes(selectedElementFromLayer.id)) {
            console.log('INCOMPATIBILITÉ TROUVÉE:', element.name, 'est incompatible avec', selectedElementFromLayer.name);
            isValid = false;
            break;
          }
        }
      }
      if (!isValid) break;
    }

    if (isValid) {
      console.log('DNA valide trouvé');
      return dnaSequence.join(DNA_DELIMITER);
    }

    attempts++;
  }

  console.log('Impossible de trouver un DNA valide après', maxAttempts, 'tentatives');
  throw new Error('Unable to generate a valid NFT with current restrictions');
};

// Build metadata
const constructMetadata = (
  dna: string,
  edition: number,
  attributesList: { trait_type: string; value: string }[],
  config: AppConfig,
  customId?: number
): NFTMetadata => {
  const dateTime = Date.now();
  
  // Filter attributes to exclude those with the value "NONE"
  const filteredAttributes = attributesList.filter(attr => 
    !attr.value.toUpperCase().includes('NONE')
  );
  
  // Use the custom ID in the name if provided, otherwise use the edition number
  const displayId = customId !== undefined ? customId : edition;
  
  // Format the image URI based on whether baseURI is provided
  const imageURI = config.baseURI 
    ? `ipfs://${config.baseURI}/${edition}.png` 
    : `${edition}.png`;
  
  const metadata: NFTMetadata = {
    name: `${config.namePrefix} #${displayId}`,
    description: config.description,
    image: imageURI,
    dna: sha1(dna).toString(),
    edition,
    date: dateTime,
    attributes: filteredAttributes,
    compiler: "NFT Art Engine",
    ...config.extraMetadata,
  };
  
  return metadata;
};

// Draw image from layers
const drawImage = async (
  selectedElements: { layer: Layer; element: LayerElement }[],
  config: AppConfig
): Promise<string> => {
  const canvas = document.createElement('canvas');
  canvas.width = config.format.width;
  canvas.height = config.format.height;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    throw new Error("Could not get canvas context");
  }
  
  ctx.imageSmoothingEnabled = config.format.smoothing;
  
  // Draw each element
  for (const { layer, element } of selectedElements) {
    // Skip elements named "NONE" or similar
    if (!element.image || element.name.toUpperCase().includes('NONE')) continue;
    
    try {
      // Create bitmap from image with error handling
      const image = await createImageBitmap(element.image).catch(error => {
        console.error(`Error decoding image "${element.name}" in layer "${layer.name}":`, error);
        throw new Error(`The image "${element.name}" in layer "${layer.name}" could not be decoded. Check the format and integrity of the image.`);
      });
      
      // Apply blend mode and opacity
      ctx.globalCompositeOperation = layer.blend as GlobalCompositeOperation;
      ctx.globalAlpha = layer.opacity;
      
      // Draw the image
      ctx.drawImage(image, 0, 0, config.format.width, config.format.height);
      
      // Reset parameters
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1.0;
    } catch (error) {
      // Propagate error with additional information
      throw error;
    }
  }
  
  return canvas.toDataURL('image/png');
};

// Function to resize a unique image to the configuration dimensions
const resizeUniqueImage = async (imageDataUrl: string, config: AppConfig): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = config.format.width;
      canvas.height = config.format.height;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error("Could not get canvas context"));
        return;
      }
      
      ctx.imageSmoothingEnabled = config.format.smoothing;
      ctx.drawImage(img, 0, 0, config.format.width, config.format.height);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = imageDataUrl;
  });
};

// Generate collection of images
export const generateCollection = async (
  layers: Layer[],
  config: AppConfig,
  uniqueNFTs: UniqueNFT[],
  onProgress: (progress: number) => void
): Promise<GeneratedImage[]> => {
  const dnaList = new Set<string>();
  const results: GeneratedImage[] = [];
  
  // Calculate the number of procedurally generated NFTs to create
  const totalUniqueNFTs = uniqueNFTs.length;
  const totalEditions = config.layerConfigurations[0].growEditionSizeTo;
  const proceduralyGeneratedCount = totalEditions - totalUniqueNFTs;
  
  if (proceduralyGeneratedCount < 0) {
    throw new Error(`The number of unique NFTs (${totalUniqueNFTs}) exceeds the total size of the collection (${totalEditions})`);
  }
  
  // Separate unique NFTs with custom IDs from those without
  const uniqueNFTsWithCustomId = uniqueNFTs.filter(nft => nft.customId !== undefined);
  const uniqueNFTsWithoutCustomId = uniqueNFTs.filter(nft => nft.customId === undefined);
  
  // Validate that custom IDs are within the collection range
  for (const nft of uniqueNFTsWithCustomId) {
    if (nft.customId! <= 0 || nft.customId! > totalEditions) {
      throw new Error(`The custom ID ${nft.customId} for NFT "${nft.name}" is outside the valid range (1-${totalEditions})`);
    }
  }
  
  // Check for duplicate custom IDs
  const customIds = uniqueNFTsWithCustomId.map(nft => nft.customId);
  const uniqueCustomIds = new Set(customIds);
  if (customIds.length !== uniqueCustomIds.size) {
    throw new Error('There are duplicate custom IDs. Each custom ID must be unique.');
  }
  
  // Create an array to store unique NFT positions
  const uniquePositions: number[] = [];
  
  // Add custom IDs to positions
  uniqueNFTsWithCustomId.forEach(nft => {
    uniquePositions.push(nft.customId!);
  });
  
  // Generate random positions for unique NFTs without custom IDs
  const remainingPositionsNeeded = uniqueNFTsWithoutCustomId.length;
  
  // Create an array of all available positions (1 to totalEditions)
  // that are not already taken by NFTs with custom IDs
  const availablePositions = Array.from(
    { length: totalEditions }, 
    (_, i) => i + 1
  ).filter(pos => !uniqueNFTsWithCustomId.some(nft => nft.customId === pos));
  
  // Shuffle the available positions array to get random positions
  const shuffledPositions = availablePositions.sort(() => Math.random() - 0.5);
  
  // Take the first 'remainingPositionsNeeded' positions from the shuffled array
  const randomPositions = shuffledPositions.slice(0, remainingPositionsNeeded);
  
  // Add these random positions to uniquePositions
  uniquePositions.push(...randomPositions);
  
  // Sort positions for easier processing
  uniquePositions.sort((a, b) => a - b);
  
  let editionCount = 1;
  let failedCount = 0;
  const maxFailedCount = config.uniqueDnaTorrance;
  
  // Create an array to store unique NFTs with their positions
  const uniqueNFTsWithPositions = [
    // First, add NFTs with custom IDs at their specified positions
    ...uniqueNFTsWithCustomId.map(nft => ({
      nft,
      position: nft.customId!
    })),
    // Then, add NFTs without custom IDs at the random positions we generated
    ...uniqueNFTsWithoutCustomId.map((nft, index) => ({
      nft,
      position: randomPositions[index]
    }))
  ];
  
  // Sort by position for easier processing
  uniqueNFTsWithPositions.sort((a, b) => a.position - b.position);
  
  // Generate the complete collection
  while (editionCount <= totalEditions) {
    // Check if this position is reserved for a unique NFT
    const uniqueNFTForPosition = uniqueNFTsWithPositions.find(item => item.position === editionCount);
    
    if (uniqueNFTForPosition) {
      // This is a position for a unique NFT
      const uniqueNFT = uniqueNFTForPosition.nft;
      
      if (!uniqueNFT.imageDataUrl) {
        throw new Error(`The unique NFT "${uniqueNFT.name}" has no image`);
      }
      
      // Resize the unique image to the configuration dimensions
      const resizedImageDataUrl = await resizeUniqueImage(uniqueNFT.imageDataUrl, config);
      
      // Create metadata for the unique NFT
      const metadata: NFTMetadata = {
        name: uniqueNFT.name && uniqueNFT.name !== config.namePrefix 
          ? uniqueNFT.name 
          : `${config.namePrefix} #${uniqueNFT.customId || editionCount}`,
        description: uniqueNFT.description || config.description,
        image: config.baseURI 
          ? `ipfs://${config.baseURI}/${editionCount}.png` 
          : `${editionCount}.png`,
        dna: sha1(`unique-${uniqueNFT.id}`).toString(),
        edition: editionCount,
        date: Date.now(),
        attributes: uniqueNFT.attributes,
        compiler: "NFT Art Engine",
        ...config.extraMetadata,
      };
      
      results.push({
        imageDataUrl: resizedImageDataUrl,
        metadata,
      });
      
      editionCount++;
      onProgress((editionCount - 1) / totalEditions * 100);
    } else {
      // This is a position for a procedurally generated NFT
      const dna = createDna(layers);
      
      if (isDnaUnique(dnaList, dna)) {
        dnaList.add(dna);
        
        // Build attributes and select elements
        const attributesList: { trait_type: string; value: string }[] = [];
        const selectedElements: { layer: Layer; element: LayerElement }[] = [];
        
        const dnaSegments = dna.split(DNA_DELIMITER);
        for (const segment of dnaSegments) {
          const [layerId, elementId] = segment.split(':').map(Number);
          
          const layer = layers.find(l => l.id === layerId);
          if (!layer) continue;
          
          const element = layer.elements.find(e => e.id === elementId);
          if (!element) continue;
          
          // Add attribute to metadata even if it's NONE
          attributesList.push({
            trait_type: layer.name,
            value: element.name,
          });
          
          // Always add element to selected elements list
          selectedElements.push({ layer, element });
        }
        
        // Generate image
        const imageDataUrl = await drawImage(selectedElements, config);
        
        // Create metadata
        const metadata = constructMetadata(dna, editionCount, attributesList, config, undefined);
        
        results.push({
          imageDataUrl,
          metadata,
        });
        
        editionCount++;
        onProgress((editionCount - 1) / totalEditions * 100);
        failedCount = 0;
      } else {
        failedCount++;
        if (failedCount >= maxFailedCount) {
          throw new Error(`Couldn't find unique DNA after ${maxFailedCount} tries`);
        }
      }
    }
  }
  
  // Verify that all unique NFTs were included in the collection
  const includedUniqueNFTsCount = uniqueNFTsWithPositions.length;
  if (includedUniqueNFTsCount !== uniqueNFTs.length) {
    console.warn(`Warning: Only ${includedUniqueNFTsCount} out of ${uniqueNFTs.length} unique NFTs were included in the collection.`);
  }
  
  return results;
};

// Check if an image is valid
const isImageValid = async (file: File | null): Promise<boolean> => {
  if (!file) return false;
  
  // Allow files named "NONE" or similar to represent the absence of an element
  if (file.name.toUpperCase().includes('NONE')) {
    return true;
  }
  
  try {
    // Create URL object for the image
    const url = URL.createObjectURL(file);
    
    // Create a promise that resolves when the image is loaded or rejected if it fails
    return await new Promise<boolean>((resolve) => {
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(true);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(false);
      };
      img.src = url;
    });
  } catch (error) {
    console.error("Error checking image:", error);
    return false;
  }
};

// Prepare layers with loaded images
export const prepareLayersForGeneration = async (
  layers: Layer[],
  config: AppConfig
): Promise<Layer[]> => {
  const preparedLayers: Layer[] = [];
  
  for (const layer of layers) {
    // Filter elements without images
    const validElements: LayerElement[] = [];
    
    for (const element of layer.elements) {
      if (element.image) {
        // Check if image is valid
        const isValid = await isImageValid(element.image);
        if (isValid) {
          validElements.push(element);
        } else {
          console.warn(`Invalid image skipped: "${element.name}" in layer "${layer.name}"`);
        }
      }
    }
    
    // Only add layer if it has valid elements
    if (validElements.length > 0) {
      preparedLayers.push({
        ...layer,
        elements: validElements,
      });
    }
  }
  
  return preparedLayers;
};

// Create CSV for OpenSea
const createOpenSeaCSV = (generatedImages: GeneratedImage[]): string => {
  // Create header row with mandatory and common attribute columns
  const allAttributes = new Set<string>();
  
  // Collect all unique attribute names
  generatedImages.forEach(({ metadata }) => {
    metadata.attributes.forEach(attr => {
      allAttributes.add(attr.trait_type);
    });
  });
  
  // Create header row
  const attributeColumns = Array.from(allAttributes).map(attr => `attributes[${attr}]`);
  const headerRow = ['tokenID', 'name', 'description', 'file_name', 'external_url', ...attributeColumns].join(',');
  
  // Create data rows
  const dataRows = generatedImages.map(({ metadata }) => {
    const tokenID = metadata.edition;
    const name = metadata.name;
    const description = metadata.description || '';
    const fileName = `${metadata.edition}.png`;
    const externalUrl = '';
    
    // Create a map of attribute values
    const attributeMap = new Map<string, string>();
    metadata.attributes.forEach(attr => {
      attributeMap.set(attr.trait_type, attr.value);
    });
    
    // Fill in attribute values or leave empty if not present
    const attributeValues = Array.from(allAttributes).map(attr => {
      const value = attributeMap.get(attr) || '';
      // Escape commas and quotes in the value
      return `"${value.replace(/"/g, '""')}"`;
    });
    
    return [tokenID, `"${name.replace(/"/g, '""')}"`, `"${description.replace(/"/g, '""')}"`, fileName, externalUrl, ...attributeValues].join(',');
  });
  
  // Combine header and data rows
  return [headerRow, ...dataRows].join('\n');
};

// Create ZIP with images and metadata
export const createDownloadableZip = async (
  generatedImages: GeneratedImage[]
): Promise<Blob> => {
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  
  // Create folders
  const imagesFolder = zip.folder('images');
  const jsonFolder = zip.folder('json');
  
  if (!imagesFolder || !jsonFolder) {
    throw new Error("Failed to create folders in zip");
  }
  
  // Add images
  for (const { imageDataUrl, metadata } of generatedImages) {
    const base64Data = imageDataUrl.replace(/^data:image\/png;base64,/, '');
    imagesFolder.file(`${metadata.edition}.png`, base64Data, { base64: true });
    jsonFolder.file(`${metadata.edition}.json`, JSON.stringify(metadata, null, 2));
  }
  
  // Add _metadata.json file
  const metadataArray = generatedImages.map(item => item.metadata);
  jsonFolder.file('_metadata.json', JSON.stringify(metadataArray, null, 2));
  
  // Add OpenSea CSV file
  const openSeaCSV = createOpenSeaCSV(generatedImages);
  zip.file('metadata-opensea.csv', openSeaCSV);
  
  // Generate ZIP
  return await zip.generateAsync({ type: 'blob' });
}; 